// ── Schedule Page ─────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { Btn, Card, Icon, Toggle } from "../components";

const DAYS       = ["mon","tue","wed","thu","fri","sat","sun"];
const DAY_LABELS = { mon:"Mon",tue:"Tue",wed:"Wed",thu:"Thu",fri:"Fri",sat:"Sat",sun:"Sun" };
const TIMEZONES  = [
  "Asia/Karachi","Asia/Kolkata","Asia/Dubai","Asia/Riyadh","Asia/Dhaka",
  "Asia/Colombo","Asia/Kabul","Europe/London","Europe/Berlin","Europe/Istanbul",
  "America/New_York","America/Chicago","America/Los_Angeles","America/Toronto",
  "Africa/Cairo","Africa/Lagos","Australia/Sydney","UTC",
];

const DEFAULT_SCHED = {
  enabled: false, timezone: "Asia/Karachi",
  days: { mon:true,tue:true,wed:true,thu:true,fri:true,sat:false,sun:false },
  startTime: "09:00", endTime: "18:00",
  offlineMessage: "We are currently offline. We will reply during business hours.",
};

export function SchedulePage({ token, showToast }) {
  const [sched,   setSched]   = useState(DEFAULT_SCHED);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/schedule", {}, token)
      .then(d => { if (d.schedule) setSched(d.schedule); })
      .finally(() => setLoading(false));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch("/api/schedule", { method: "POST", body: JSON.stringify(sched) }, token);
      showToast?.("Schedule saved ✓");
    } catch { showToast?.("Failed to save schedule", "error"); }
    setSaving(false);
  };

  const setDay = (day, val) => setSched(s => ({ ...s, days: { ...s.days, [day]: val } }));
  const inp = {
    padding: "10px 14px", background: T.bg0, border: `1px solid ${T.border1}`,
    borderRadius: 9, color: T.text1, fontSize: 13, outline: "none", fontFamily: base.font,
  };

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 26, height: 26, border: `2px solid ${T.border2}`, borderTopColor: T.emerald, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "26px 28px 40px" }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: T.text1 }}>Auto-Reply Schedule</div>
        <div style={{ fontSize: 13, color: T.text3, marginTop: 3 }}>Control when the AI bot automatically responds to messages</div>
      </div>

      <Card style={{ marginBottom: 20 }}>
        {/* Enable toggle */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0", marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text1 }}>Schedule Control</div>
            <div style={{ fontSize: 13, color: T.text3, marginTop: 3 }}>When enabled, bot only replies during configured hours</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 13, color: sched.enabled ? T.emerald : T.text3, fontWeight: 600 }}>
              {sched.enabled ? "✓ Active" : "Off"}
            </span>
            <Toggle value={sched.enabled} onChange={v => setSched(s => ({ ...s, enabled: v }))} />
          </div>
        </div>

        {/* Status indicator */}
        {sched.enabled && (
          <div style={{ padding: "10px 14px", borderRadius: 10, background: T.emeraldDim, border: `1px solid rgba(99,102,241,0.2)`, fontSize: 12, color: T.emerald, marginBottom: 20 }}>
            ✅ Schedule is enabled — bot will only reply during the hours and days below
          </div>
        )}
        {!sched.enabled && (
          <div style={{ padding: "10px 14px", borderRadius: 10, background: T.bg0, border: `1px solid ${T.border1}`, fontSize: 12, color: T.text3, marginBottom: 20 }}>
            ℹ️ Schedule is off — bot replies 24/7 regardless of time
          </div>
        )}

        {/* Timezone + hours */}
        <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, color: T.text3, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Timezone</label>
            <select value={sched.timezone} onChange={e => setSched(s => ({ ...s, timezone: e.target.value }))} style={{ ...inp, width: "100%" }}>
              {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: T.text3, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Active hours</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="time" value={sched.startTime} onChange={e => setSched(s => ({ ...s, startTime: e.target.value }))} style={{ ...inp, flex: 1, fontFamily: base.mono }} />
              <span style={{ color: T.text3 }}>to</span>
              <input type="time" value={sched.endTime} onChange={e => setSched(s => ({ ...s, endTime: e.target.value }))} style={{ ...inp, flex: 1, fontFamily: base.mono }} />
            </div>
          </div>
        </div>

        {/* Day picker */}
        <div style={{ marginBottom: 22 }}>
          <label style={{ display: "block", fontSize: 11, color: T.text3, marginBottom: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Active days</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {DAYS.map(day => (
              <div key={day} onClick={() => setDay(day, !sched.days?.[day])}
                style={{ padding: "7px 18px", borderRadius: 20, cursor: "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.15s", background: sched.days?.[day] ? T.emerald : T.bg0, color: sched.days?.[day] ? "#fff" : T.text3, border: `1px solid ${sched.days?.[day] ? T.emerald : T.border1}`, userSelect: "none" }}>
                {DAY_LABELS[day]}
              </div>
            ))}
          </div>
        </div>

        {/* Offline message */}
        <div style={{ marginBottom: 22 }}>
          <label style={{ display: "block", fontSize: 11, color: T.text3, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Offline message
          </label>
          <div style={{ fontSize: 12, color: T.text3, marginBottom: 8 }}>Sent to customers who message outside your active hours</div>
          <textarea value={sched.offlineMessage} onChange={e => setSched(s => ({ ...s, offlineMessage: e.target.value }))}
            style={{ ...inp, width: "100%", minHeight: 80, resize: "vertical", lineHeight: 1.65, boxSizing: "border-box" }}
            onFocus={e => (e.target.style.borderColor = T.emerald)}
            onBlur={e  => (e.target.style.borderColor = T.border1)} />
        </div>

        <Btn variant="primary" onClick={save} disabled={saving}>
          {saving
            ? <><div style={{ width: 13, height: 13, border: "2px solid rgba(0,0,0,0.2)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} /> Saving...</>
            : <><Icon name="upload" size={13} color="#fff" /> Save Schedule</>}
        </Btn>
      </Card>

      {/* How it works */}
      <Card>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 16 }}>How it works</div>
        {[
          ["Schedule enabled",          "Bot checks the current time before every reply",          "emerald"],
          ["Within hours + active day", "AI replies normally to all incoming messages",            "sky"    ],
          ["Outside hours or off day",  "Sends your offline message instead of an AI reply",       "amber"  ],
          ["Human mode",                "Always lets you reply manually, ignores schedule entirely","violet" ],
          ["Schedule disabled",         "Bot replies 24/7 with no time restrictions",              "teal"   ],
        ].map(([title, desc, color], i, arr) => (
          <div key={i} style={{ display: "flex", gap: 14, padding: "11px 0", borderBottom: i < arr.length - 1 ? `1px solid ${T.border1}` : "none" }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: T[color], marginTop: 5, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: T.text1 }}>{title}</div>
              <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>{desc}</div>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

export default SchedulePage;